import React from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";

const SectionHeader = ({ icon, title }) => (
  <Grid item xs={12}>
    <Box display="flex" alignItems="center" gap={0.75} mt={1.5} mb={0.25}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        letterSpacing={0.8}
        textTransform="uppercase"
      >
        {title}
      </Typography>
      <Box flex={1} height="1px" bgcolor="divider" ml={0.5} />
    </Box>
  </Grid>
);

const FieldCard = ({ label, value, xs = 3 }) => (
  <Grid item xs={xs}>
    <Box
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        height: "100%",
        transition: "all 0.15s ease",
        "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        textTransform="uppercase"
        letterSpacing={0.4}
        display="block"
        fontSize="0.6rem"
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        fontSize="0.8rem"
        color={value != null && value !== "" ? "text.primary" : "text.disabled"}
      >
        {value != null && value !== "" ? value : "N/A"}
      </Typography>
    </Box>
  </Grid>
);

const ViewCooler = ({ cooler }) => {
  if (!cooler) return null;

  const formatDate = (val) => (val ? new Date(val).toLocaleString() : null);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700} letterSpacing={-0.3}>
            Cooler Details
          </Typography>
          <Typography variant="caption" color="text.secondary">
            — Inventory Record
          </Typography>
        </Box>
        <Chip
          label={cooler.isCheckedIn ? "Checked In" : "Checked Out"}
          color={cooler.isCheckedIn ? "success" : "warning"}
          size="small"
          icon={<AcUnitIcon sx={{ fontSize: 14 }} />}
        />
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Grid container spacing={1}>
        {/* Identity */}
        <SectionHeader
          icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
          title="Cooler Identity"
        />
        <FieldCard label="Cooler ID (Barcode)" value={cooler.coolerId} />
        <FieldCard label="Location" value={cooler.locationName} />
        <FieldCard
          label="Profile"
          value={cooler.coolerProfile?.profileName ?? null}
        />
        <FieldCard
          label="Profile Size"
          value={cooler.coolerProfile?.coolerSize ?? null}
        />

        {/* Client */}
        <SectionHeader
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          title="Client Information"
        />
        <FieldCard
          label="Client Account #"
          value={cooler.clientAccountNumber}
        />

        {/* Check-In / Check-Out */}
        <SectionHeader
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          title="Check-In / Check-Out"
        />
        <FieldCard
          label="Last Check-In"
          value={formatDate(cooler.lastCheckInTime)}
        />
        <FieldCard
          label="Last Check-Out"
          value={formatDate(cooler.lastCheckOutTime)}
        />

        {/* Audit Trail */}
        <SectionHeader
          icon={<HistoryIcon sx={{ fontSize: 14 }} />}
          title="Audit Trail"
        />
        <FieldCard label="Created At" value={formatDate(cooler.createdAt)} />
        <FieldCard label="Created By" value={cooler.createdBy} />
        {cooler.updatedAt && (
          <FieldCard
            label="Updated At"
            value={formatDate(cooler.updatedAt)}
          />
        )}
        {cooler.updatedBy && (
          <FieldCard label="Updated By" value={cooler.updatedBy} />
        )}
      </Grid>
    </Paper>
  );
};

export default ViewCooler;